import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { AuthResult, OtpRequestResult } from '@/lib/types';
import { useAppDispatch } from '@/redux/hooks';
import { signedIn } from '@/redux/session-slice';
import { Button, Field, Notice, OrRule, TextButton } from '@/components/ui';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { Logo } from '@/components/Brand';

type Step = 'email' | 'code';

/**
 * Screen 1 — sign in.
 *
 * Work email with a one-time code, and Continue with Google.
 *
 * There is deliberately NO "Sign in with Microsoft", even though the next
 * screens will connect a Microsoft 365 calendar. ZenCore does not support
 * Microsoft as an identity provider, so the button could only ever fail. The
 * two things are unrelated: who you are is ZenSpace's business, whose calendar
 * you are connecting is a separate decision made later. A note on this screen
 * says so, because an admin arriving to connect Microsoft 365 will look for it.
 */
export function SignIn() {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState<OtpRequestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setFieldError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await api<OtpRequestResult>('/auth/otp/request', {
        method: 'POST',
        anonymous: true,
        withoutOrganization: true,
        data: { identifier: email.trim(), type: 'email' },
      });
      setSession(result);
      setStep('code');
    } catch (err) {
      // ZenCore's status codes come through unchanged, so a 400 here really
      // does mean "that address is not one we know", not "we are broken".
      setFieldError(
        err instanceof ApiError
          ? err.message
          : 'We could not send a code just now. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: React.FormEvent) {
    event.preventDefault();
    if (!session) return;
    setFieldError(null);
    setBusy(true);
    try {
      const result = await api<AuthResult>('/auth/otp/verify', {
        method: 'POST',
        anonymous: true,
        withoutOrganization: true,
        data: { session_id: session.session_id, code: code.trim() },
      });
      dispatch(signedIn(result));
    } catch (err) {
      setFieldError(
        err instanceof ApiError
          ? err.message
          : 'That code could not be checked. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setCode('');
    setFieldError(null);
    setBusy(true);
    try {
      const result = await api<OtpRequestResult>('/auth/otp/request', {
        method: 'POST',
        anonymous: true,
        withoutOrganization: true,
        data: { identifier: email.trim(), type: 'email' },
      });
      setSession(result);
      setNotice(`We have sent a new code to ${email.trim()}.`);
    } catch (err) {
      setFieldError(err instanceof ApiError ? err.message : 'We could not send a new code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <HeroPanel />

      <section className="flex items-center justify-center p-[28px] sm:p-[40px]">
        <div className="animate-rise flex w-full max-w-[400px] flex-col gap-[20px]">
          <div>
            <p className="zs-eyebrow m-0 text-green-mid">Administrator sign in</p>
            <h1 className="mt-[8px] mb-0 text-heading font-semibold">
              {step === 'email' ? 'Welcome back' : 'Check your email'}
            </h1>
            <p className="mt-[6px] mb-0 text-sub text-muted">
              {step === 'email'
                ? 'Use the work email address you sign in to ZenSpace with.'
                : `We sent a six-digit code to ${email.trim()}. It expires shortly.`}
            </p>
          </div>

          {notice && <Notice tone="healthy" title={notice} />}

          {step === 'email' ? (
            <form onSubmit={requestCode} className="flex flex-col gap-[20px]" noValidate>
              <Field
                label="Work email"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                required
                autoFocus
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={fieldError}
              />
              <Button type="submit" tone="primary" block busy={busy} disabled={!email.trim()}>
                Email me a code
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="flex flex-col gap-[20px]" noValidate>
              <Field
                label="Six-digit code"
                name="one-time-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                autoFocus
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                error={fieldError}
              />
              <Button type="submit" tone="primary" block busy={busy} disabled={code.length < 4}>
                Sign in
              </Button>
              <div className="flex items-center justify-between">
                <TextButton
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setFieldError(null);
                    setNotice(null);
                  }}
                >
                  Use a different email
                </TextButton>
                <TextButton type="button" onClick={resend} disabled={busy}>
                  Send a new code
                </TextButton>
              </div>
            </form>
          )}

          <OrRule />

          <GoogleSignInButton
            onSignedIn={(result) => dispatch(signedIn(result))}
            onError={setFieldError}
          />

          <p className="m-0 text-caption text-muted">
            Signing in with Microsoft is not available — ZenSpace accounts use email or Google. You
            can still connect a Microsoft 365 calendar once you are in.
          </p>
        </div>
      </section>
    </main>
  );
}

/**
 * The one place in the product that uses the hero gradient.
 *
 * A photograph over it is optional: drop one at `public/images/hero-pod.jpg`
 * and it appears, blended down to a texture. Without it the gradient carries
 * the panel on its own rather than leaving a broken image in the corner of the
 * sign-in screen.
 */
function HeroPanel() {
  const [photo, setPhoto] = useState(true);

  return (
    <section className="zs-hero zs-on-dark relative hidden flex-col justify-between overflow-hidden p-[48px] py-[40px] text-page lg:flex">
      {photo && (
        <img
          src="/images/hero-pod.jpg"
          alt=""
          aria-hidden="true"
          onError={() => setPhoto(false)}
          className="absolute inset-0 h-full w-full object-cover opacity-[0.22] mix-blend-luminosity"
        />
      )}
      {/* A quiet wash of light from the top-left, so the panel has depth even
          when no photograph has been supplied. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_0%_0%,rgba(255,255,255,0.18),transparent_60%)]"
      />

      <Logo width={163} className="relative" />

      <div className="relative flex max-w-[520px] flex-col gap-[18px]">
        <p className="zs-eyebrow m-0 text-green-soft">ZenCore · Calendar adapter</p>
        <h2 className="m-0 text-display font-semibold">
          Your rooms, your calendar — one connection.
        </h2>
        <p className="m-0 text-lead text-sky-mist">
          Connect Microsoft 365 or Google Workspace, map each resource calendar to a ZenCore space,
          and let your people book pods from the calendar they already use.
        </p>
      </div>

      <p className="zs-tagline relative m-0 text-subheading text-sky-mist">
        Hardware-agnostic · No on-premises software · Least-privilege scopes
      </p>
    </section>
  );
}
